import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { TOverlayRef } from '@/common/components/Overlay/Overlay.types';
import { OverlayBottom } from '@/common/components/Overlay/OverlayBottom/OverlayBottom';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import {
  INTL_MONTH_LONG_FORMAT,
  formatDate,
  intlFormat,
  now,
} from '@/common/utils/date';
import { DatePicker as HNDatePicker } from '@coverflex-tech/hypernova';
import { set } from 'date-fns';
import { IDatePickerProps, TDatePickerRef } from './DatePicker.types';

export const DatePicker = forwardRef(
  (props: IDatePickerProps, ref: React.Ref<TDatePickerRef>) => {
    const { t } = useTranslation();
    const bottomOverlayRef = useRef<TOverlayRef>(null);
    const calendarRef = useRef<{
      setOpen: (open: boolean) => void;
    } | null>(null);
    const breakpoints = useBreakpoints(['md']);
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      setOpen: (open: boolean) => {
        calendarRef.current?.setOpen(open);
        setIsOpen(open);
      },
    }));

    const picker = (
      <HNDatePicker
        {...props}
        inline={!breakpoints.md}
        onChange={(date) => {
          props.onChange(date);
          bottomOverlayRef.current?.close();
        }}
        ref={calendarRef}
        i18nKeys={{
          previous: t('general.navigation.previous'),
          next: t('general.navigation.next'),
        }}
        formatMonth={(month: number) =>
          intlFormat(set(now(), { month }), INTL_MONTH_LONG_FORMAT)
        }
        formatWeekDay={(day: string) => t(`calendar.${day.toLowerCase()}`)}
        reactDatePickerProps={{
          popperModifiers: [
            {
              name: 'offset',
              options: {
                offset: [-16, 10],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                rootBoundary: 'viewport',
              },
            },
          ],
          portalId: 'primitives-root',
        }}
      />
    );

    if (breakpoints.md) {
      return (
        <View
          className={props.disabled ? 'text-neutral20' : 'text-neutral30'}
          onClick={(evt) => evt.stopPropagation()}
        >
          {picker}
        </View>
      );
    }

    return (
      <>
        <BodyText bold color={props.disabled ? 'neutral20' : 'neutral30'}>
          {formatDate(props.value)}
        </BodyText>
        {isOpen && (
          <OverlayBottom
            onClose={() => setIsOpen(false)}
            ref={bottomOverlayRef}
          >
            <Stack align="center" className="pb-lg">
              {picker}
            </Stack>
          </OverlayBottom>
        )}
      </>
    );
  }
);

DatePicker.displayName = 'DatePicker';
